/**
 * Text-to-path conversion using opentype.js (loaded from CDN at first use).
 *
 * Font files are sourced from the @fontsource packages on jsDelivr as WOFF1
 * (.woff) — Google Fonts returns WOFF2 for modern browsers and opentype.js
 * does not support WOFF2, so the Google Fonts CSS API is intentionally avoided.
 * URL pattern: cdn.jsdelivr.net/npm/@fontsource/{slug}@4/files/{slug}-latin-{weight}-{style}.woff
 */

const OPENTYPE_CDN = 'https://cdn.jsdelivr.net/npm/opentype.js@1.3.4/dist/opentype.min.js'

let _opentypePromise = null

async function loadOpentype () {
  if (_opentypePromise) return _opentypePromise
  _opentypePromise = (async () => {
    if (window.opentype) return window.opentype

    const resp = await fetch(OPENTYPE_CDN)
    if (!resp.ok) throw new Error(`Failed to fetch opentype.js from CDN (HTTP ${resp.status})`)
    const src = await resp.text()

    // Execute the UMD bundle with module/exports/define masked as undefined so
    // the UMD wrapper falls through to the browser-global assignment
    // (root['opentype'] = factory()) rather than the CommonJS branch.
    // eslint-disable-next-line no-new-func
    new Function('module', 'exports', 'define', src)(undefined, undefined, undefined)

    if (!window.opentype) throw new Error('opentype.js executed but window.opentype is not set')
    return window.opentype
  })()
  return _opentypePromise
}

// Cache loaded Font objects keyed by "family|weight|style"
const fontCache = new Map()

function normalizeWeight (weight) {
  if (weight === 'bold') return '700'
  if (weight === 'normal') return '400'
  const n = parseInt(weight, 10)
  return String(isNaN(n) ? 400 : n)
}

// The SVG editor only offers Google Fonts, so most font values are already
// direct fontsource slugs. This map only needs to handle CSS generic family
// keywords that could appear in SVG content created outside the editor.
const FONT_FAMILY_MAP = {
  'serif':       'Lora',
  'sans-serif':  'Roboto',
  'monospace':   'Roboto Mono',
  'cursive':     'Dancing Script',
  'fantasy':     'Cinzel Decorative',
}

function resolveFontFamily (family) {
  return FONT_FAMILY_MAP[family.toLowerCase()] || family
}

function normalizeFontFamily (family) {
  return family.split(',')[0].trim().replace(/['"]/g, '')
}

/** Build the jsDelivr @fontsource WOFF1 URL for a given font. */
function fontsourceWoffUrl (fontName, weight, style) {
  const slug = fontName.toLowerCase().replace(/\s+/g, '-')
  const styleStr = style === 'italic' ? 'italic' : 'normal'
  return `https://cdn.jsdelivr.net/npm/@fontsource/${slug}@4/files/${slug}-latin-${weight}-${styleStr}.woff`
}

/** Try to load a font via opentype.load; returns null instead of throwing. */
async function tryLoad (opentype, url) {
  try {
    return await opentype.load(url)
  } catch {
    return null
  }
}

async function loadFont (family, weight, style) {
  const key = `${family}|${weight}|${style}`
  if (fontCache.has(key)) return fontCache.get(key)

  const opentype = await loadOpentype()
  const resolved = resolveFontFamily(family)

  // Try: resolved font at requested weight/style → resolved at 400/normal → Roboto 400 normal
  const font =
    await tryLoad(opentype, fontsourceWoffUrl(resolved, weight, style)) ||
    await tryLoad(opentype, fontsourceWoffUrl(resolved, '400', 'normal')) ||
    await tryLoad(opentype, fontsourceWoffUrl('Roboto', '400', 'normal'))

  if (!font) throw new Error(`Could not load a font for "${family}" — check your internet connection`)

  fontCache.set(key, font)
  return font
}

/**
 * Convert a single SVG <text> element (including <tspan> children) to a
 * compound SVG path `d` string.  Throws on failure so callers can show
 * the real error to the user.
 *
 * @param {SVGTextElement} textEl
 * @returns {Promise<string|null>}
 */
export async function textElementToPathData (textEl) {
  const cs = window.getComputedStyle(textEl)

  const family = normalizeFontFamily(
    cs.fontFamily || textEl.getAttribute('font-family') || 'sans-serif'
  )
  const weight = normalizeWeight(
    cs.fontWeight || textEl.getAttribute('font-weight') || '400'
  )
  const style  = (cs.fontStyle || textEl.getAttribute('font-style') || 'normal').trim()
  const size   = parseFloat(cs.fontSize || textEl.getAttribute('font-size') || '16')
  const anchor = (cs.textAnchor || textEl.getAttribute('text-anchor') || 'start').trim()

  const font = await loadFont(family, weight, style)

  // Collect (text, x, y) segments
  const segments = []
  const tspans   = Array.from(textEl.querySelectorAll('tspan'))

  if (tspans.length === 0) {
    const x = parseFloat(textEl.getAttribute('x') || 0)
    const y = parseFloat(textEl.getAttribute('y') || 0)
    segments.push({ text: textEl.textContent, x, y })
  } else {
    let curX = parseFloat(textEl.getAttribute('x') || 0)
    let curY = parseFloat(textEl.getAttribute('y') || 0)
    for (const tspan of tspans) {
      if (tspan.hasAttribute('x'))  curX  = parseFloat(tspan.getAttribute('x'))
      if (tspan.hasAttribute('y'))  curY  = parseFloat(tspan.getAttribute('y'))
      if (tspan.hasAttribute('dy')) curY += parseFloat(tspan.getAttribute('dy'))
      const text = tspan.textContent
      if (text.trim()) segments.push({ text, x: curX, y: curY })
    }
  }

  if (segments.length === 0) return null

  let combinedD = ''
  for (const { text, x, y } of segments) {
    if (!text.trim()) continue
    let drawX = x
    if (anchor === 'middle' || anchor === 'end') {
      const advance = font.getAdvanceWidth(text, size)
      drawX = anchor === 'middle' ? x - advance / 2 : x - advance
    }
    combinedD += font.getPath(text, drawX, y, size).toPathData(4)
  }

  return combinedD || null
}
